import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { SnippetBox, ToggleRow } from '../../../styles';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { COLORS } from '@/styles/colors';
import { Typography } from '@/components/Typography/Typography';
import { Mono } from '@/styles/common';
import { SwitchToggle } from '@/components/form/fields/SwitchToggle/SwitchToggle';
import { Checkbox } from '@/components/form/fields/Checkbox/Checkbox';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteWebhook, loadWebhookDeliveries, rotateWebhookSecret, updateWebhook } from '../../../api/api';
import { WebhookEndpointType } from '@/types/invoiceVaultTypes';
import { useState } from 'react';
import { Notification } from '@/components/Notification/Notification';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';

type WebhooksEndpointsType = {
  endpoints;
  secretOnce: { id: string; secret: string } | null;
  onChanged: () => void;
  canEdit: boolean;
  events: string[];
  onSetSecret: (secret: { id: string; secret: string }) => void;
};

export const WebhooksEndpoints = ({
  endpoints = [],
  secretOnce,
  onChanged,
  canEdit,
  events = [],
  onSetSecret,
}: WebhooksEndpointsType) => {
  const [showDeliveries, setShowDeliveries] = useState<string | null>(null);
  const [remove, setRemove] = useState<WebhookEndpointType | null>(null);

  const toggleEvent = useMutation({
    mutationFn: ({ endpoint, event, on }: { endpoint: WebhookEndpointType; event: string; on: boolean }) =>
      updateWebhook(endpoint.id, {
        url: endpoint.url,
        events: on ? [...endpoint.events, event] : endpoint.events.filter((e) => e !== event),
        is_active: endpoint.is_active,
      }),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      onChanged();
    },
  });

  const toggleActive = useMutation({
    mutationFn: (endpoint: WebhookEndpointType) =>
      updateWebhook(endpoint.id, { url: endpoint.url, events: endpoint.events, is_active: !endpoint.is_active }),
    onSuccess: () => onChanged(),
  });

  const rotate = useMutation({
    mutationFn: rotateWebhookSecret,
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      onSetSecret({ id: response.body.id, secret: response.body.secret });
      onChanged();
    },
  });

  const del = useMutation({
    mutationFn: deleteWebhook,
    onSuccess() {
      setRemove(null);
      onChanged();
    },
  });

  const { data: deliveriesResponse } = useQuery({
    queryKey: ['invoice_vault_webhook_deliveries', showDeliveries],
    queryFn: () => loadWebhookDeliveries(showDeliveries as string),
    enabled: !!showDeliveries,
  });

  return (
    <Box display='flex' gap='20px' flexDirection='column' marginTop='24px'>
      {endpoints.map((endpoint) => (
        <Box
          display='flex'
          flexDirection='column'
          gap='20px'
          key={endpoint.id}
          border={`1px solid ${COLORS.grey[400]}`}
          borderRadius='12px'
          padding='16px'
          background={COLORS.grey[150]}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center' gap='12px' width={'100%'}>
            <Box minWidth='0'>
              <Mono>
                <b>{endpoint.url}</b>
              </Mono>

              <Typography variant='bodySmall2'>
                Secret {endpoint.secret_masked} · added {formatDate(endpoint.date_created, DATE_TIME_FORMAT)}
              </Typography>
            </Box>
            <SwitchToggle
              name={`active-${endpoint.id}`}
              checked={endpoint.is_active}
              disabled={!canEdit}
              onChange={() => toggleActive.mutate(endpoint)}
            />
          </Box>
          {secretOnce?.id === endpoint.id && (
            <Notification variant='warning'>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography>
                  Signing secret (shown once): <br />
                  <Mono>
                    <b>{secretOnce.secret}</b>
                  </Mono>{' '}
                </Typography>
                <ButtonNew size='medium' variant='secondary' onClick={() => copyToClipboard(secretOnce.secret)}>
                  Copy
                </ButtonNew>
              </Box>
            </Notification>
          )}
          <Box>
            {events.map((event) => (
              <ToggleRow key={event}>
                <Typography variant='bodySmall'>{event}</Typography>
                <Checkbox
                  name={`${endpoint.id}-${event}`}
                  checked={endpoint.events.includes(event)}
                  disabled={!canEdit}
                  onChange={(e) => toggleEvent.mutate({ endpoint, event, on: e.target.checked })}
                />
              </ToggleRow>
            ))}
          </Box>
          <Box display='flex' gap='16px' flexWrap='wrap'>
            <ButtonNew
              size='medium'
              variant='secondary'
              onClick={() => setShowDeliveries(showDeliveries === endpoint.id ? null : endpoint.id)}
            >
              {showDeliveries === endpoint.id ? 'Hide deliveries' : 'Recent deliveries'}
            </ButtonNew>
            {canEdit && (
              <>
                <ButtonNew size='medium' variant='secondary' onClick={() => rotate.mutate(endpoint.id)}>
                  Rotate secret
                </ButtonNew>
                <ButtonNew size='medium' variant='danger' onClick={() => setRemove(endpoint)}>
                  Remove
                </ButtonNew>
              </>
            )}
          </Box>
          {showDeliveries === endpoint.id && (
            <SnippetBox>
              {(deliveriesResponse?.body?.results ?? []).length === 0
                ? 'No deliveries yet.'
                : (deliveriesResponse?.body?.results ?? [])
                    .map(
                      (d) =>
                        `${formatDate(d.date_created, DATE_TIME_FORMAT)}  ${d.event.padEnd(28)} ${d.status.padEnd(10)} attempts ${d.attempts}${d.last_status_code ? ` http ${d.last_status_code}` : ''}${d.last_error ? `  ${d.last_error}` : ''}`
                    )
                    .join('\n')}
            </SnippetBox>
          )}
        </Box>
      ))}

      {remove && (
        <ConfirmModal
          isOpen
          header='Remove this endpoint?'
          body={`${remove.url} stops receiving events immediately.`}
          confirmText='Remove'
          confirmButtonVariant='danger'
          isLoading={del.isPending}
          onConfirm={() => del.mutate(remove.id)}
          onClose={() => setRemove(null)}
        />
      )}
    </Box>
  );
};
